import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { MetaScopeOptions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import type { Source } from '@core/tags-and-sources-types';

import { EntityStoreKey } from '../../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Source>()(
  EntityStoreKey.Sources,
  {
    customActions: adapter => ({
      createSource: (
        state,
        payload: {
          candidateId?: number;
          sourceName: string | string[];
        },
      ) => adapter.markAsPending(state),
      createSourceSuccess: (state, payload: Source[]) =>
        pipeState(adapter.addMany(state, payload), next => adapter.markAsLoaded(next)),
      deleteCandidateSources: (
        state,
        payload: {
          candidateId: number;
          sourceName: string | string[];
        },
      ) => adapter.markAsPending(state),
      deleteCandidateSourcesSuccess: (state, payload: Source[]) =>
        pipeState(
          adapter.removeMany(
            state,
            payload.map(source => source.id),
          ),
          next => adapter.markAsLoaded(next),
        ),
      deleteSources: (state, payload: Array<Source['id']>) => adapter.markAsPending(state),
      deleteSourcesSuccess: (state, payload: number[]) =>
        pipeState(adapter.removeMany(state, payload), next => adapter.markAsLoaded(next)),
      updateSource: (
        state,
        payload: {
          sourceId: number;
          sourceName: string;
        },
      ) => adapter.markAsPending(state),
      updateSourceSuccess: (state, payload: Source) =>
        pipeState(adapter.updateOne(state, payload), next => adapter.markAsLoaded(next)),
      updateSourceFailed: ngrxEvent<HttpErrorResponse>(),
      mergeSources: (
        state,
        payload: {
          sourceIds: number[];
          sourceName: string;
        },
      ) => adapter.markAsPending(state),
      mergeSourcesSuccess: (state, payload: { source: Source; sourceIds: number[] }) =>
        pipeState(
          adapter.removeMany(state, payload.sourceIds),
          next => adapter.addOne(next, payload.source),
          next => adapter.markAsLoaded(next),
        ),
    }),
  },
);

const paginatedAttributeSelector = memoizeSelector((props: MetaScopeOptions) =>
  createSelector(
    selectors.pagination(props),
    selectors.entities(props),
    (pagination, entities) => ({
      meta: pagination,
      rows: entities.map(({ id, name, taggingsCount, status, searchableName }) => ({
        id,
        count: taggingsCount,
        value: name,
        status,
        searchableName,
      })),
    }),
  ),
);

const sourcesSelectors = { ...selectors, paginatedAttributeSelector };

export {
  actions as SourcesActions,
  reducer as SourcesReducer,
  sourcesSelectors,
  namespace as SourcesNamespace,
};
